import processProxy from '@/env/processProxy';

export type ResolveChildBaseUrlOptions = {
  envBaseUrl?: string;
  devUrl: string;
  testingUrl?: string;
  fallback?: string;
  forceDev?: boolean;
  customBaseUrl?: string;
  normalize?: boolean;
};

function normalizeParsedBaseUrl(url: string, fallback: string): string {
  const trimmed = url.trim();
  if (!trimmed) return fallback;
  try {
    const u = new URL(trimmed);
    const path = u.pathname.replace(/\/$/, '') || '';
    if (!path || path === '/') return u.origin;
    return `${u.origin}${path}`;
  } catch {
    return fallback;
  }
}

/**
 * Generic resolver for a child-app base URL using the project's env conventions
 * (DEV / Vercel preview can override with a hardcoded testing URL or a custom one).
 */
export function resolveChildBaseUrl(options: ResolveChildBaseUrlOptions): string {
  const {
    envBaseUrl = '',
    devUrl,
    testingUrl,
    fallback,
    forceDev,
    customBaseUrl,
    normalize = true,
  } = options;

  const isLocal = processProxy.VUE_APP_ENV === 'DEV';
  const isVercel = processProxy.VUE_APP_VERCEL_ENV === 'preview';
  const isVercelOrLocal = isVercel || isLocal;

  let baseUrl = envBaseUrl;

  if (isLocal && forceDev) {
    baseUrl = devUrl;
  } else if (isVercelOrLocal && (customBaseUrl || testingUrl)) {
    baseUrl = customBaseUrl || testingUrl || baseUrl;
  }

  if (!baseUrl?.trim() && fallback) baseUrl = fallback;

  return normalize ? normalizeParsedBaseUrl(baseUrl, fallback ?? baseUrl) : baseUrl;
}

export function originFromBaseUrl(baseUrl: string, label?: string): string {
  try {
    return new URL(baseUrl).origin;
  } catch {
    if (processProxy.VUE_APP_ENV === 'DEV') {
      // eslint-disable-next-line no-console
      console.warn(`[${label ?? 'iframe'}] Invalid base URL for origin:`, baseUrl);
    }
    return typeof window !== 'undefined' ? window.location.origin : '';
  }
}
